import { isEmpty, isNil } from 'lodash';

import useDataFetcher from '~/components/useDataFetcher';

interface PolicyLimitBreach {
  reason: string;
}

interface FetchPaymentPossibilityRet {
  isLoadingPaymentIssues: boolean;
  isErrorPaymentIssues: boolean;
  policyLimitBreaches: PolicyLimitBreach[];
  isPaymentIssuePreventingPayment: boolean | null;
}

interface PaymentPossibilityResponse {
  is_valid: boolean | null;
  policy_limit_breaches: PolicyLimitBreach[];
  approve_option: string | null;
}

interface PaymentRequestParams {
  amount: number;
  payment_type: string;
  should_include_pending_payments: boolean;
  expense_sub_reserve_amounts?: string;
}

export default function useFetchPaymentPossibility(
  claimId: number,
  exposureIdOrNull: number | null,
  payableType: string,
  amount: number,
  expenseSubReserveAmounts?: Array<{ type_key: string; amount: number }>
): FetchPaymentPossibilityRet {
  // we only have sub-expenses that might effect policy limits on a specific coverage
  // so we only need to fetch if we have an exposure and expenseSubReserveAmounts
  const shouldFetch =
    !isNil(exposureIdOrNull) &&
    amount > 0 &&
    (payableType === 'indemnity' ||
      (payableType === 'expenses' &&
        expenseSubReserveAmounts &&
        !isEmpty(expenseSubReserveAmounts) &&
        expenseSubReserveAmounts.every((item) => !isEmpty(item.type_key))));

  // Build request parameters
  const params: PaymentRequestParams = {
    amount,
    payment_type: payableType,
    should_include_pending_payments: false,
  };

  // Add expense-specific parameters
  if (payableType === 'expenses' && expenseSubReserveAmounts) {
    params.expense_sub_reserve_amounts = JSON.stringify(expenseSubReserveAmounts);
  }

  const {
    isLoading: isLoadingPaymentIssues,
    isError: isErrorPaymentIssues,
    data: {
      is_valid: paymentIsValid,
      policy_limit_breaches: policyLimitBreaches,
      approve_option: paymentIssuesApproveOption,
    } = {
      is_valid: null,
      policy_limit_breaches: [],
      approve_option: null,
    },
  } = useDataFetcher<PaymentPossibilityResponse>(
    shouldFetch ? `/api/v1/claims/${claimId}/exposures/${exposureIdOrNull}/payment_possibility` : '',
    { params },
    shouldFetch
  );
  const isPaymentIssuePreventingPayment = !paymentIsValid && paymentIssuesApproveOption === 'none';

  if (!shouldFetch) {
    return {
      isLoadingPaymentIssues: false,
      isErrorPaymentIssues: false,
      policyLimitBreaches: [],
      isPaymentIssuePreventingPayment: null,
    };
  }
  return { isLoadingPaymentIssues, isErrorPaymentIssues, policyLimitBreaches, isPaymentIssuePreventingPayment };
}
